(function () {
  'use strict';

  /* ============================================================
     DateUtils — timezone-safe, date-only helpers
     ============================================================ */
  var DateUtils = {
    nowInZone: function (timeZone, minuteOffset, serverTimestamp) {
      minuteOffset = minuteOffset || 0;
      var now = serverTimestamp ? new Date(serverTimestamp * 1000) : new Date();
      now.setMinutes(now.getMinutes() + minuteOffset);

      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', hour12: false
      }).formatToParts(now);

      var map = {};
      for (var i = 0; i < parts.length; i++) map[parts[i].type] = parts[i].value;

      var year = Number(map.year);
      var month = Number(map.month) - 1;
      var day = Number(map.day);
      var hour = Number(map.hour) % 24;
      var minute = Number(map.minute);

      return {
        date: new Date(Date.UTC(year, month, day)),
        hour: hour,
        minute: minute
      };
    },

    createDateInZone: function (year, month, day, timeZone) {
      var low = new Date(Date.UTC(year, month, day - 1));
      var high = new Date(Date.UTC(year, month, day + 1));

      for (var i = 0; i < 40; i++) {
        var mid = new Date((low.getTime() + high.getTime()) / 2);

        var parts = new Intl.DateTimeFormat('en-US', {
          timeZone: timeZone,
          year: 'numeric', month: 'numeric', day: 'numeric'
        }).formatToParts(mid);

        var map = {};
        for (var j = 0; j < parts.length; j++) map[parts[j].type] = parts[j].value;

        var tzYear = Number(map.year);
        var tzMonth = Number(map.month) - 1;
        var tzDay = Number(map.day);

        if (tzYear < year || (tzYear === year && tzMonth < month) || (tzYear === year && tzMonth === month && tzDay < day)) {
          low = mid;
        } else if (tzYear > year || (tzYear === year && tzMonth > month) || (tzYear === year && tzMonth === month && tzDay > day)) {
          high = mid;
        } else {
          return mid;
        }
      }

      return new Date((low.getTime() + high.getTime()) / 2);
    },

    addDays: function (date, days) {
      var d = new Date(date);
      d.setUTCDate(d.getUTCDate() + days);
      return d;
    },

    sameDay: function (a, b) {
      return a.getUTCFullYear() === b.getUTCFullYear() &&
             a.getUTCMonth() === b.getUTCMonth() &&
             a.getUTCDate() === b.getUTCDate();
    },

    formatYYYYMMDD: function (date) {
      var m = String(date.getUTCMonth() + 1).padStart(2, '0');
      var d = String(date.getUTCDate()).padStart(2, '0');
      return date.getUTCFullYear() + '-' + m + '-' + d;
    },

    formatDisplay: function (date) {
      return date.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    },

    parseYYYYMMDD: function (str) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(str || '')) return null;
      var p = str.split('-');
      var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
      return isNaN(d.getTime()) ? null : d;
    },

    monthLabel: function (date) {
      var year = date.getUTCFullYear();
      var month = date.getUTCMonth();
      var tempDate = new Date(Date.UTC(year, month, 1));
      return tempDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    },

    getDayOfWeekInZone: function (date) {
      return date.getUTCDay();
    }
  };

  /* ============================================================
     Holidays — US federal blackout dates, generated per year
     ============================================================ */
  var Holidays = (function () {
    var cache = {};

    function nthWeekday(year, month, weekday, n) {
      var d = new Date(Date.UTC(year, month, 1));
      var offset = (weekday - d.getUTCDay() + 7) % 7;
      return new Date(Date.UTC(year, month, 1 + offset + (n - 1) * 7));
    }

    function lastWeekday(year, month, weekday) {
      var d = new Date(Date.UTC(year, month + 1, 0));
      var offset = (d.getUTCDay() - weekday + 7) % 7;
      return new Date(Date.UTC(year, month, d.getUTCDate() - offset));
    }

    function forYear(year) {
      if (cache[year]) return cache[year];

      var list = [
        new Date(Date.UTC(year, 0, 1)),
        lastWeekday(year, 4, 1),
        new Date(Date.UTC(year, 6, 4)),
        nthWeekday(year, 8, 1, 1),
        nthWeekday(year, 10, 4, 4),
        new Date(Date.UTC(year, 11, 25))
      ];

      var set = {};
      for (var i = 0; i < list.length; i++) set[DateUtils.formatYYYYMMDD(list[i])] = true;
      cache[year] = set;
      return set;
    }

    return {
      isHoliday: function (date) {
        return !!forYear(date.getUTCFullYear())[DateUtils.formatYYYYMMDD(date)];
      }
    };
  })();

  /* ============================================================
     CartAPI — Shopify Ajax Cart
     ============================================================ */
  var CartAPI = {
    fetchCart: function () {
      return Promise.race([
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) {
            if (!r.ok) throw new Error('Cart fetch failed');
            return r.json();
          }),
        new Promise(function (_, reject) {
          setTimeout(function () { reject(new Error('Timeout')); }, 1000);
        })
      ]);
    },

    saveDeliveryDate: function (attributeName, yyyymmdd) {
      var attributes = {};
      attributes[attributeName] = yyyymmdd;

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).then(function (r) {
        if (!r.ok) throw new Error('Cart update failed');
        return r.json();
      });
    }
  };

  /* ============================================================
     CartProfile — product classification via tags / product type
     ============================================================ */
  var CartProfile = (function () {
    var productCache = {};

    function classifyText(text) {
      var haystack = (text || '').toLowerCase();
      if (haystack.indexOf('non-perishable') !== -1 || haystack.indexOf('non perishable') !== -1) {
        return 'non-perishable';
      }
      if (haystack.indexOf('perishable') !== -1) return 'perishable';
      if (haystack.indexOf('frozen') !== -1) return 'frozen';
      return null;
    }

    function classifyItem(item) {
      var byType = classifyText(item.product_type);
      if (byType) return Promise.resolve(byType);
      
      return Promise.resolve('non-perishable');
    }

    function profileFromCart(cart) {
      var items = (cart && cart.items) || [];
      return Promise.all(items.map(classifyItem)).then(buildProfile);
    }

    function buildProfile(classes) {
      return {
        empty: classes.length === 0,
        classes: classes,
        containsPerishable: classes.indexOf('perishable') !== -1,
        containsOnlyFrozen: classes.length > 0 && classes.every(function (c) { return c === 'frozen'; }),
        containsOnlyNonPerishable: classes.length > 0 && classes.every(function (c) { return c === 'non-perishable'; }),
        containsOnlyFrozenOrNonPerishable: classes.length > 0 && classes.every(function (c) {
          return c === 'frozen' || c === 'non-perishable';
        })
      };
    }

    return {
      fromCart: profileFromCart,
      cartContainsPerishable: function (profile) { return profile.containsPerishable; },
      cartContainsOnlyFrozen: function (profile) { return profile.containsOnlyFrozen; },
      cartContainsOnlyNonPerishable: function (profile) { return profile.containsOnlyNonPerishable; }
    };
  })();

  /* ============================================================
     Shipping — zone + estimated arrival abstractions
     ============================================================ */
  var Shipping = {
    getShippingZone: function () {
      return Promise.resolve('FedEx Zone 3');
    },

    getEstimatedArrivalDate: function (fulfillmentDate) {
      return Promise.resolve(DateUtils.addDays(fulfillmentDate, 2));
    }
  };

  /* ============================================================
     Rules — blackout logic + earliest selectable date engine
     ============================================================ */
  function createRules(customBlackoutDates, timeZone) {
    var customBlackoutSet = {};
    if (customBlackoutDates && Array.isArray(customBlackoutDates)) {
      for (var i = 0; i < customBlackoutDates.length; i++) {
        customBlackoutSet[customBlackoutDates[i]] = true;
      }
    }

    timeZone = timeZone || 'America/New_York';

    return {
      isSunday: function (date) {
        return DateUtils.getDayOfWeekInZone(date) === 0;
      },

      isMonday: function (date) {
        return DateUtils.getDayOfWeekInZone(date) === 1;
      },

      isSaturday: function (date) {
        return DateUtils.getDayOfWeekInZone(date) === 6;
      },

      isHoliday: function (date) {
        return Holidays.isHoliday(date);
      },

      isCustomBlackout: function (date) {
        return !!customBlackoutSet[DateUtils.formatYYYYMMDD(date)];
      },

      isBlackout: function (date) {
        return this.isSunday(date) || this.isMonday(date) || this.isSaturday(date) ||
               this.isHoliday(date) || this.isCustomBlackout(date);
      },

      isPastCutoff: function (now, cutoffHour, cutoffMinute) {
        return now.hour > cutoffHour ||
               (now.hour === cutoffHour && now.minute >= cutoffMinute);
      },

      getValidFulfillmentDate: function (now, cutoffHour, cutoffMinute) {
        var d = this.isPastCutoff(now, cutoffHour, cutoffMinute)
          ? DateUtils.addDays(now.date, 1)
          : now.date;

        while (this.isBlackout(d)) d = DateUtils.addDays(d, 1);
        return d;
      },

      getEarliestSelectableDate: function (fulfillmentDate, profile, zone) {
        var self = this;
        return Promise.resolve(this.rollPastBlackouts(fulfillmentDate));
      },

      rollPastBlackouts: function (date) {
        var d = date;
        while (this.isBlackout(d)) d = DateUtils.addDays(d, 1);
        return d;
      },

      isSelectable: function (date, earliest, latest) {
        return date >= earliest && date <= latest && !this.isBlackout(date);
      }
    };
  }

  /* ============================================================
     Calendar — rendering, navigation, selection, accessibility
     ============================================================ */
  function DeliveryCalendar(root) {
    this.root = root;
    this.input = root.querySelector('[data-ddp-input]');
    this.popup = root.querySelector('[data-ddp-popup]');
    this.grid = root.querySelector('[data-ddp-grid]');
    this.title = root.querySelector('[data-ddp-title]');
    this.status = root.querySelector('[data-ddp-status]');
    this.prevBtn = root.querySelector('[data-ddp-prev]');
    this.nextBtn = root.querySelector('[data-ddp-next]');

    var customDatesAttr = root.dataset.customBlackoutDates || '';
    var customDates = customDatesAttr.trim() !== '' 
      ? customDatesAttr.split(',').map(function (s) { return s.trim(); })
      : [];

    this.config = {
      cutoffHour: Number(root.dataset.cutoffHour || 10),
      cutoffMinute: Number(root.dataset.cutoffMinute || 0),
      timeZone: root.dataset.timezone || 'America/New_York',
      attributeName: root.dataset.attributeName || 'Requested Delivery Date',
      monthsVisible: Number(root.dataset.monthsVisible || 7),
      customBlackoutDates: customDates,
      timeOffsetMinutes: Number(root.dataset.timeOffsetMinutes || 0)
    };

    this.Rules = createRules(this.config.customBlackoutDates, this.config.timeZone);

    this.ready = false;
    this.readyPromise = null;
    this.earliest = null;
    this.latest = null;
    this.viewYear = null;
    this.viewMonth = null;
    this.selected = null;

    this.bindTriggers();

    if (this.input.value.trim() !== '') this.init();
  }

  DeliveryCalendar.prototype = {
    bindTriggers: function () {
      var self = this;

      this.root.addEventListener('click', function (e) {
        var input = e.target.closest('[data-ddp-input]');
        if (input) self.toggle();
      });

      this.root.addEventListener('keydown', function (e) {
        var input = e.target.closest('[data-ddp-input]');
        if (input) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            self.open();
          }
          if (e.key.length === 1) e.preventDefault();
        }

        if (e.key === 'Escape') {
          self.close();
          self.input.focus();
        }
      });

      document.addEventListener('click', function (e) {
        if (!self.root.contains(e.target)) self.close();
      });
    },

    init: function () {
      if (this.readyPromise) return this.readyPromise;

      var self = this;
      var serverTimestamp = this.root.dataset.serverTimestamp ? Number(this.root.dataset.serverTimestamp) : null;
      var now = DateUtils.nowInZone(this.config.timeZone, this.config.timeOffsetMinutes, serverTimestamp);

      this.today = now.date;

      var dynamicBlackout = [DateUtils.formatYYYYMMDD(this.today)];
      if (this.Rules.isPastCutoff(now, this.config.cutoffHour, this.config.cutoffMinute)) {
        var tomorrow = DateUtils.addDays(this.today, 1);
        dynamicBlackout.push(DateUtils.formatYYYYMMDD(tomorrow));
      }

      var allBlackoutDates = (this.config.customBlackoutDates || []).concat(dynamicBlackout);
      this.Rules = createRules(allBlackoutDates, this.config.timeZone);

      var fulfillment = this.Rules.getValidFulfillmentDate(now, this.config.cutoffHour, this.config.cutoffMinute);

      this.readyPromise = Promise.all([CartAPI.fetchCart(), Shipping.getShippingZone()])
        .then(function (results) {
          var cart = results[0];
          var zone = results[1];

          var timeStr = String(now.hour).padStart(2, '0') + ':' + String(now.minute).padStart(2, '0');
          console.log('Current time (' + self.config.timeZone + '):', timeStr);

          var isPastCutoff = self.Rules.isPastCutoff(now, self.config.cutoffHour, self.config.cutoffMinute);
          var todayDate = DateUtils.formatYYYYMMDD(now.date);
          var storageKey = 'ddp_cutoff_cleared_' + self.config.attributeName.replace(/\s+/g, '_');
          var lastCleared = localStorage.getItem(storageKey);

          if (isPastCutoff && lastCleared !== todayDate) {
            console.log('Past cutoff (' + self.config.cutoffHour + ':' + String(self.config.cutoffMinute).padStart(2, '0') + ') — clearing saved delivery date');
            if (cart.attributes && cart.attributes[self.config.attributeName]) {
              CartAPI.saveDeliveryDate(self.config.attributeName, '').catch(function () {});
            }
            try {
              localStorage.setItem(storageKey, todayDate);
            } catch (e) {}
          }

          var saved = DateUtils.parseYYYYMMDD(cart.attributes && cart.attributes[self.config.attributeName]);

          return CartProfile.fromCart(cart).then(function (profile) {
            return self.Rules.getEarliestSelectableDate(fulfillment, profile, zone);
          }).then(function (earliest) {
            self.earliest = earliest;
            self.latest = new Date(Date.UTC(
              self.today.getUTCFullYear(),
              self.today.getUTCMonth() + self.config.monthsVisible,
              0
            ));

            if (saved && self.Rules.isSelectable(saved, self.earliest, self.latest)) {
              self.selected = saved;
              self.input = self.root.querySelector('[data-ddp-input]');
              if (self.input) self.input.value = DateUtils.formatDisplay(saved);
            } else if (saved) {
              self.input = self.root.querySelector('[data-ddp-input]');
              if (self.input) self.input.value = '';
              CartAPI.saveDeliveryDate(self.config.attributeName, '').catch(function () {});
            }

            var start = self.selected || self.earliest;
            self.viewYear = start.getUTCFullYear();
            self.viewMonth = start.getUTCMonth();
            self.ready = true;
          });
        })
        .catch(function () {
          self.earliest = self.Rules.rollPastBlackouts(fulfillment);
          self.latest = new Date(Date.UTC(
            self.today.getUTCFullYear(),
            self.today.getUTCMonth() + self.config.monthsVisible,
            0
          ));
          self.viewYear = self.earliest.getUTCFullYear();
          self.viewMonth = self.earliest.getUTCMonth();
          self.ready = true;
        });

      return this.readyPromise;
    },

    toggle: function () {
      if (this.popup.hidden) this.open();
      else this.close();
    },

    open: function () {
      var self = this;
      
      this.readyPromise = null;
      this.ready = false;
      
      this.init().then(function () {
        self.input = self.root.querySelector('[data-ddp-input]');
        self.popup = self.root.querySelector('[data-ddp-popup]');
        self.grid = self.root.querySelector('[data-ddp-grid]');
        self.title = self.root.querySelector('[data-ddp-title]');
        self.prevBtn = self.root.querySelector('[data-ddp-prev]');
        self.nextBtn = self.root.querySelector('[data-ddp-next]');
        self.status = self.root.querySelector('[data-ddp-status]');

        if (!self.popup || !self.grid) return;

        self.renderCalendar();
        self.popup.hidden = false;

        var rect = self.input.getBoundingClientRect();
        var needed = self.popup.offsetHeight + 16;
        self.popup.classList.toggle('ddp__popup--up',
          window.innerHeight - rect.bottom < needed && rect.top > needed);

        self.input.setAttribute('aria-expanded', 'true');
        self.focusFirstSelectable();
        self.bindNavOnce();
      }).catch(function () {
        self.renderCalendar();
        self.popup.hidden = false;
        self.input.setAttribute('aria-expanded', 'true');
        self.focusFirstSelectable();
        self.bindNavOnce();
      });
    },

    close: function () {
      this.input = this.root.querySelector('[data-ddp-input]');
      this.popup = this.root.querySelector('[data-ddp-popup]');
      
      if (this.popup) this.popup.hidden = true;
      if (this.input) this.input.setAttribute('aria-expanded', 'false');
    },

    bindNavOnce: function () {
      var self = this;
      
      this.root.removeEventListener('click', this.navClickHandler);
      this.root.removeEventListener('keydown', this.navKeydownHandler);
      
      this.navClickHandler = function (e) {
        var prevBtn = e.target.closest('[data-ddp-prev]');
        var nextBtn = e.target.closest('[data-ddp-next]');
        var dateBtn = e.target.closest('button[data-date]');
        
        if (prevBtn) self.changeMonth(-1);
        if (nextBtn) self.changeMonth(1);
        if (dateBtn && !dateBtn.disabled) self.selectDate(DateUtils.parseYYYYMMDD(dateBtn.dataset.date));
      };
      
      this.navKeydownHandler = function (e) {
        var dateBtn = e.target.closest('button[data-date]');
        if (dateBtn) self.onGridKeydown(e);
      };
      
      this.root.addEventListener('click', this.navClickHandler);
      this.root.addEventListener('keydown', this.navKeydownHandler);
    },

    monthHasSelectableDate: function (year, month) {
      var daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      for (var d = 1; d <= daysInMonth; d++) {
        var date = DateUtils.createDateInZone(year, month, d, this.config.timeZone);
        if (this.Rules.isSelectable(date, this.earliest, this.latest)) return true;
      }
      return false;
    },

    changeMonth: function (delta) {
      var y = this.viewYear;
      var m = this.viewMonth + delta;
      var targetYear = m < 0 ? y - 1 : (m > 11 ? y + 1 : y);
      var targetMonth = ((m % 12) + 12) % 12;

      if (!this.monthHasSelectableDate(targetYear, targetMonth)) return;

      this.viewYear = targetYear;
      this.viewMonth = targetMonth;
      this.renderCalendar();
      this.focusFirstSelectable();
    },

    renderCalendar: function () {
      this.title = this.root.querySelector('[data-ddp-title]');
      this.grid = this.root.querySelector('[data-ddp-grid]');
      this.prevBtn = this.root.querySelector('[data-ddp-prev]');
      this.nextBtn = this.root.querySelector('[data-ddp-next]');

      var y = this.viewYear;
      var m = this.viewMonth;
      var first = DateUtils.createDateInZone(y, m, 1, this.config.timeZone);
      var daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
      var frag = document.createDocumentFragment();

      if (this.title) this.title.textContent = DateUtils.monthLabel(first);

      for (var pad = 0; pad < DateUtils.getDayOfWeekInZone(first); pad++) {
        var empty = document.createElement('span');
        empty.className = 'ddp__day ddp__day--empty';
        empty.setAttribute('role', 'presentation');
        frag.appendChild(empty);
      }

      for (var d = 1; d <= daysInMonth; d++) {
        var date = DateUtils.createDateInZone(y, m, d, this.config.timeZone);
        var selectable = this.Rules.isSelectable(date, this.earliest, this.latest);
        var btn = document.createElement('button');

        btn.type = 'button';
        btn.className = 'ddp__day';
        btn.textContent = String(d);
        btn.dataset.date = DateUtils.formatYYYYMMDD(date);
        btn.setAttribute('role', 'gridcell');
        btn.setAttribute('aria-label', date.toLocaleDateString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
        }) + (selectable ? '' : ', unavailable'));

        if (!selectable) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.tabIndex = -1;
        } else {
          btn.tabIndex = -1;
        }

        if (this.selected && DateUtils.sameDay(date, this.selected)) {
          btn.classList.add('ddp__day--selected');
          btn.setAttribute('aria-selected', 'true');
        }
        if (this.today && DateUtils.sameDay(date, this.today)) {
          btn.classList.add('ddp__day--today');
        }

        frag.appendChild(btn);
      }

      if (this.grid) {
        this.grid.textContent = '';
        this.grid.appendChild(frag);
      }

      var prevMonth = m === 0 ? 11 : m - 1;
      var prevYear = m === 0 ? y - 1 : y;
      var nextMonth = m === 11 ? 0 : m + 1;
      var nextYear = m === 11 ? y + 1 : y;

      if (this.prevBtn) this.prevBtn.disabled = !this.monthHasSelectableDate(prevYear, prevMonth);
      if (this.nextBtn) this.nextBtn.disabled = !this.monthHasSelectableDate(nextYear, nextMonth);
    },

    focusFirstSelectable: function () {
      this.grid = this.root.querySelector('[data-ddp-grid]');
      if (!this.grid) return;

      var target = this.grid.querySelector('.ddp__day--selected:not(:disabled)') ||
                   this.grid.querySelector('button[data-date]:not(:disabled)');
      if (target) {
        target.tabIndex = 0;
        target.focus();
      }
    },

    onGridKeydown: function (e) {
      var current = e.target.closest('button[data-date]');
      if (!current) return;

      var jump = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];

      if (jump) {
        e.preventDefault();
        this.moveFocus(DateUtils.parseYYYYMMDD(current.dataset.date), jump);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!current.disabled) this.selectDate(DateUtils.parseYYYYMMDD(current.dataset.date));
      }
    },

    moveFocus: function (fromDate, delta) {
      this.grid = this.root.querySelector('[data-ddp-grid]');
      if (!this.grid) return;

      var step = delta > 0 ? Math.abs(delta) : -Math.abs(delta);
      var date = DateUtils.addDays(fromDate, step);

      for (var i = 0; i < 90; i++) {
        if (this.Rules.isSelectable(date, this.earliest, this.latest)) break;
        date = DateUtils.addDays(date, delta > 0 ? 1 : -1);
      }
      if (!this.Rules.isSelectable(date, this.earliest, this.latest)) return;

      if (date.getUTCFullYear() !== this.viewYear || date.getUTCMonth() !== this.viewMonth) {
        this.viewYear = date.getUTCFullYear();
        this.viewMonth = date.getUTCMonth();
        this.renderCalendar();
      }

      var btn = this.grid.querySelector('button[data-date="' + DateUtils.formatYYYYMMDD(date) + '"]');
      if (btn && !btn.disabled) {
        var prev = this.grid.querySelector('button[tabindex="0"]');
        if (prev) prev.tabIndex = -1;
        btn.tabIndex = 0;
        btn.focus();
      }
    },

    selectDate: function (date) {
      var self = this;

      if (!date || !this.Rules.isSelectable(date, this.earliest, this.latest)) {
        this.setStatus('That date is not available for delivery.');
        return;
      }

      this.input = this.root.querySelector('[data-ddp-input]');
      this.status = this.root.querySelector('[data-ddp-status]');

      var yyyymmdd = DateUtils.formatYYYYMMDD(date);
      this.selected = date;
      if (this.input) this.input.value = DateUtils.formatDisplay(date);
      this.renderCalendar();
      this.setStatus('Saving…');

      CartAPI.saveDeliveryDate(this.config.attributeName, yyyymmdd)
        .then(function () {
          self.setStatus('Delivery date saved.');
          self.close();
          if (self.input) self.input.focus();
        })
        .catch(function () {
          self.setStatus('Could not save. Please try again.');
        });
    },

    setStatus: function (msg) {
      this.status = this.root.querySelector('[data-ddp-status]');
      if (this.status) this.status.textContent = msg;
    }
  };

  /* ============================================================
     Boot — one instance per section on the page
     ============================================================ */
  function boot() {
    var roots = document.querySelectorAll('[data-delivery-date-picker]');
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].__ddp) roots[i].__ddp = new DeliveryCalendar(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();